import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { type ReactNode, useEffect, useState } from "react"
import { type CatalogLanguage, getCatalog, loadCatalogs } from "./catalogs"

/**
 * Renders `children` once the given catalogs are loaded, and a spinner until
 * then. For screens that read a specific language's strings during render
 * regardless of the UI language — a document's own language, say — through
 * `getCatalog()`. Pass a module-level constant as `languages`.
 */
export function CatalogGate({
  languages,
  children,
}: {
  languages: readonly CatalogLanguage[]
  children: ReactNode
}) {
  const [, setLoadedCount] = useState(0)
  const [error, setError] = useState<Error | null>(null)
  const ready = languages.every((language) => getCatalog(language))

  useEffect(() => {
    if (ready) return
    let cancelled = false
    loadCatalogs(languages).then(
      () => {
        if (!cancelled) setLoadedCount((count) => count + 1)
      },
      (reason: unknown) => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason : new Error(String(reason)))
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [languages, ready])

  // Surface a failed chunk load to the nearest error boundary.
  if (error) throw error
  if (ready) return children
  return (
    <div className="py-XL flex h-full w-full items-center justify-center">
      <LoadingSpinner />
    </div>
  )
}
